import * as React from "react";
import {none, option, Option} from "ts-option";
import {RootState} from "../../../store/reducer/root/RootState";
import {connect} from "react-redux";
import Loading from "../../ui/loading/Loading";
import {api} from "../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {OnSuccess} from "../../../store/action/actionHelpers";
import BrandDisplayer from "../../brand/BrandDisplayer";
import PartnerBrandDisplayer from "../../partnerBrand/PartnerBrandDisplayer";
import IndividualContact from "../../../model/contact/IndividualContact";
import PreferredMerchantBranchLocationDisplayer
	from "../../admin/partnerBrands/merchant/PreferredMerchantBranchLocationDisplayer";
import CustomFieldValue from "../../../model/job/CustomFieldValue";
import * as _ from "underscore";
import Lead from "../../../model/lead/Lead";
import {LeadSidebarItem} from "../../admin/sidebarConfig/LeadSidebarConfig";
import SiteAddress from "../v4/SiteAddress";
import Contact from "../v4/Contact";
import Tags from "../v4/Tags";
import Description from "../v4/leadDescription/Description";
import LeadTypeDropdown from "../v4/leadType/LeadTypeDropdown";
import LeadWatchers from "../v4/LeadWatchers";
import LeadAcquisitionChannelDropdown from "../v4/LeadAcquisitionChannelDropdown";
import LeadReasonForContact from "../v4/reasonForContact/LeadReasonForContact";
import LeadMarketingAndTracking from "../v4/marketingAndTracking/LeadMarketingAndTracking";
import LeadCustomFields from "../../job/customFields/LeadCustomFields";
import PayByFinance from "../v4/PayByFinance";

interface ComponentProps {
	/**
	 * Lead.
	 */
	lead: Option<Lead>;
	/**
	 * Contact.
	 */
	contact: Option<IndividualContact>;
}

interface ReduxProps {
	/**
	 * Lead sidebar order.
	 */
	leadSidebarOrder: Option<string[]>;
}

interface State {
	/**
	 * Lead custom fields values
	 */
	leadCustomFieldsValues: Option<CustomFieldValue[]>;
}

type Props = ComponentProps & ReduxProps;

class LeadSidebar extends React.Component<Props, State> {
	sidebarMap: { [key in LeadSidebarItem]?: any } = {
		SITE_ADDRESS: SiteAddress,
		CUSTOMER: Contact,
		MERCHANT_BRANCH_LOCATION: PreferredMerchantBranchLocationDisplayer,
		TAGS: Tags,
		DESCRIPTION: Description,
		LEAD_TYPE: LeadTypeDropdown,
		WATCHERS: LeadWatchers,
		HOW_PROSPECT_FOUND_US: LeadAcquisitionChannelDropdown,
		REASON_FOR_CONTACT: LeadReasonForContact,
		INBOUND_MARKETING_AND_TRACKING: LeadMarketingAndTracking,
		// UNUSED
		PAY_BY_FINANCE: PayByFinance
	};

	sidebarContactDetailsMap: { [key in LeadSidebarItem]?: any } = {
		BRAND: BrandDisplayer,
		PARTNER_BRAND: PartnerBrandDisplayer,
	};

	sidebarCustomFieldsDetailsMap: { [key in LeadSidebarItem]?: any } = {
		CUSTOM_FIELDS: LeadCustomFields
	};

	constructor(props: Props) {
		super(props);
		this.state = {leadCustomFieldsValues: none};
	}

	componentDidMount() {
		this.props.lead.map(j => {
			this.fetchLeadCustomFieldsValues(j.id);
		});
	}

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.lead.map(l => l.id).orNull !== nextProps.lead.map(l => l.id).orNull) {
			nextProps.lead.map(j => {
				this.fetchLeadCustomFieldsValues(j.id);
			});
		}
	}

	render() {
		return (
			<Loading isLoading={this.props.leadSidebarOrder.isEmpty || this.props.lead.isEmpty}>
				{this.props.leadSidebarOrder.getOrElse([]).map(this.renderSidebarDetails)}
			</Loading>
		);
	}

	renderSidebarDetails = (item: LeadSidebarItem): JSX.Element => {
		if (this.sidebarMap[item]) {
			return this.renderLeadComponent(item);
		}
		else if (this.sidebarContactDetailsMap[item]) {
			return this.renderContactComponent(item);
		}
		else if (this.sidebarCustomFieldsDetailsMap[item]) {
			return this.renderCustomFieldComponent(item);
		}
	};

	renderLeadComponent = (item: LeadSidebarItem): JSX.Element => {
		const Component = this.sidebarMap[item];
		if (item === LeadSidebarItem.MERCHANT_BRANCH_LOCATION) {
			return <Component item={this.props.lead} key={item}/>;
		}
		return <Component lead={this.props.lead} key={item}/>;
	};

	renderContactComponent = (item: LeadSidebarItem): JSX.Element => {
		const Component = this.sidebarContactDetailsMap[item];
		return <Component contact={this.props.contact} key={item}/>;
	};

	renderCustomFieldComponent = (item: LeadSidebarItem): JSX.Element => {
		const Component = this.sidebarCustomFieldsDetailsMap[item];
		return <Component leadCustomFieldsValues={this.state.leadCustomFieldsValues}
						  createLeadCustomFieldValue={this.createLeadCustomFieldValue}
						  updateLeadCustomFieldValue={this.updateLeadCustomFieldValue}
						  removeLeadCustomFieldValue={this.removeLeadCustomFieldValue}
						  key={item}/>;
	};

	fetchLeadCustomFieldsValues = (leadId: string) => {
		this.setState({leadCustomFieldsValues: none}, () => api.instance.leadApi.fetchLeadCustomFieldsValues(leadId)
			.then(leadCustomFieldsValues => this.setState({leadCustomFieldsValues: option(leadCustomFieldsValues)}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch lead custom fields values")))
	};

	createLeadCustomFieldValue = (createdCustomFieldValue: CustomFieldValue, onSuccess: OnSuccess) => {
		this.props.lead.map(lead => api.instance.leadApi.createLeadCustomFieldValue(lead.id, createdCustomFieldValue)
			.then((customFieldValue) => {
				let customFieldsValues = this.state.leadCustomFieldsValues.getOrElse([]);
				customFieldsValues.push(customFieldValue);
				this.setState({leadCustomFieldsValues: option(customFieldsValues)}, onSuccess);
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Create lead custom field value")));
	};

	updateLeadCustomFieldValue = (updatedCustomFieldValue: CustomFieldValue, onSuccess: OnSuccess) => {
		api.instance.leadApi.updateLeadCustomFieldValue(updatedCustomFieldValue)
			.then((customFieldValue) => {
				let customFieldsValues = this.state.leadCustomFieldsValues.getOrElse([]);
				const updatedIndex = _.findIndex(customFieldsValues,
					(item) => item.customFieldId === customFieldValue.customFieldId);
				if (updatedIndex > -1) {
					customFieldsValues[updatedIndex] = customFieldValue;
					this.setState({leadCustomFieldsValues: option(customFieldsValues)}, onSuccess);
				}
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Update lead custom field value"));
	};

	removeLeadCustomFieldValue = (customFieldValueId: string, onSuccess: OnSuccess) => {
		api.instance.leadApi.deleteLeadCustomFieldValue(customFieldValueId)
			.then(() => {
				let customFieldsValues = this.state.leadCustomFieldsValues.getOrElse([]);
				const deletedIndex = _.findIndex(customFieldsValues, (item) => item.id.get === customFieldValueId);
				if (deletedIndex > -1) {
					customFieldsValues.splice(deletedIndex, 1);
					this.setState({leadCustomFieldsValues: option(customFieldsValues)}, onSuccess);
				}
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Delete lead custom field value"));
	};
}

function mapStateToProps(state: RootState) {
	return {
		leadSidebarOrder: state.sidebarOrder.leadSidebarOrder
	}
}

export default connect(mapStateToProps)(LeadSidebar)